// src/autopilotMode/utils/ModelCardUploadButton.js
// File upload button that triggers the enhanced progress modal

import React, { useState, useRef } from 'react';
import { Button, Text, Group } from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import ModelCardUploadProgress from '../components/ModelCardUploadProgress';
import { uploadViaSupabase } from '../../utils/supabaseUpload';
import { authFetch } from '../../utils/authFetch';

export default function ModelCardUploadButton({ 
  modelId, 
  assessmentUuid, 
  onSuccess, 
  onError,
  variant = "outline",
  size = "md",
  children 
}) {
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    progress: 0,
    stage: 'upload',
    message: '',
    error: null
  });
  
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = '';

    startUpload(file);
  };

  const startUpload = async (file) => {
    setUploadState({
      isUploading: true,
      progress: 0,
      stage: 'upload',
      message: 'Starting upload...',
      error: null
    });

    try {
      // Step 1: Upload to Supabase (0-50%)
      setUploadState(prev => ({
        ...prev,
        progress: 10,
        message: 'Uploading to secure storage...'
      }));

      const bucket = 'model-cards';
      const path = `${modelId}/${Date.now()}-${file.name}`;
      
      const uploadResult = await uploadViaSupabase({ 
        file, 
        bucket, 
        path, 
        contentType: file.type || 'application/pdf' 
      });

      setUploadState(prev => ({
        ...prev,
        progress: 50,
        message: 'Upload complete, processing...'
      }));

      setUploadState(prev => ({
        ...prev,
        progress: 50,
        message: 'Processing and creating embeddings...'
      }));

      // Step 2: Start LlamaParse job
      const startRes = await authFetch('/.netlify/functions/supaLlamaStart', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket: uploadResult.bucket, path: uploadResult.path, fileName: file.name })
      });
      if (!startRes.ok) throw new Error(await startRes.text().catch(()=> 'Parse start failed'));
      const { jobId } = await startRes.json();

      setUploadState(prev => ({
        ...prev,
        progress: 70,
        message: 'Creating searchable content...'
      }));

      // Step 3: Poll finalize until succeeded
      let attempts = 0;
      while (attempts < 30) {
        const finRes = await authFetch('/.netlify/functions/supaLlamaFinalize', {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId, bucket: uploadResult.bucket, path: uploadResult.path, fileName: file.name, modelId, assessmentUuid, saveMarkdown: true })
        });
        if (finRes.status === 202) {
          attempts += 1;
          const delayMs = Math.min(5000, 1000 * Math.pow(1.5, attempts));
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }
        if (!finRes.ok) {
          const txt = await finRes.text().catch(()=> 'Finalize failed');
          throw new Error(txt);
        }
        break;
      }

      setUploadState(prev => ({
        ...prev,
        progress: 100,
        stage: 'complete',
        message: 'Upload and processing complete'
      }));

      const result = {
        success: true,
        bucket: uploadResult.bucket,
        path: uploadResult.path,
        uploadResult
      };

      // Success callback
      onSuccess?.(result);

    } catch (error) {
      console.error('Model card upload failed:', error);
      setUploadState(prev => ({
        ...prev,
        stage: 'error',
        error: error.message,
        progress: 0
      }));
      
      onError?.(error);
    }
  };

  const handleClose = () => {
    if (uploadState.stage === 'complete' || uploadState.stage === 'error') {
      setUploadState({
        isUploading: false,
        progress: 0,
        stage: 'upload',
        message: '',
        error: null
      });
    }
  };

  const handleCancel = () => {
    // For now, just close the modal
    // In the future, we could implement actual upload cancellation
    handleClose();
  };

  const handleRetry = () => {
    // Trigger file picker again
    fileInputRef.current?.click();
  };

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt,.csv,.json,.md,.doc,.docx"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Upload trigger button */}
      <Button
        variant={variant}
        size={size}
        leftIcon={<IconUpload size={16} />}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadState.isUploading}
        loading={uploadState.isUploading}
      >
        {children || (
          <Group spacing="xs">
            <Text>Upload Model Card</Text>
          </Group>
        )}
      </Button>

      {/* Progress modal */}
      <ModelCardUploadProgress
        opened={uploadState.isUploading}
        onClose={handleClose}
        stage={uploadState.stage}
        progress={uploadState.progress}
        message={uploadState.message}
        error={uploadState.error}
        onCancel={uploadState.stage === 'error' ? handleRetry : handleCancel}
      />
    </>
  );
}